import React from 'react';
import { animated, SpringValues } from '@react-spring/web';
import { RenderElementState, Section } from './types';
import classNames from 'classnames';
import { createPropsEqualsChecker } from './utils/create-props-equals-checker';
import { useEditorStore } from './editor-store';

type Props = {
  section: Section;
  style: SpringValues<RenderElementState> | RenderElementState;
  setContextMenuState: (sectionId: Section['id'], pageX: number, pageY: number) => void;
  onAddImage: (sectionId: Section['id']) => void;
  onAddText: (sectionId: Section['id']) => void;
};

export const EmptySectionView = ({ section, style, setContextMenuState, onAddImage, onAddText }: Props) => {
  const insertionBoundary = useEditorStore((state) => state.insertionBoundary);
  const sections = useEditorStore((state) => state.layout.sections);
  let draggingOver = false;
  if (insertionBoundary && insertionBoundary.sectionIndex < sections.length) {
    const insertionSection = sections[insertionBoundary.sectionIndex];
    draggingOver = insertionSection.id === section.id;
  }
  return (
    <div className='absolute'>
      <animated.div
        className={classNames(
          'group absolute flex-none rounded-2xl origin-center',
          'transition-[color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,filter,backdrop-filter]'
        )}
        style={style}
        onContextMenu={(e) => {
          e.preventDefault(); // prevent the default behaviour when right clicked
          e.stopPropagation();
          console.log('Right Click');
          setContextMenuState(section.id, e.pageX, e.pageY);
        }}
      >
        <div
          className={classNames('absolute top-0 left-0 w-full bg-neue-canvas-bg rounded-lg transition', {
            'h-[80px] -translate-y-[40px]': section.tainted,
            'h-[120px] -translate-y-[40px]': !section.tainted,
          })}
        ></div>
        {!section.tainted && (
          <div
            className={classNames(
              'absolute h-full top-0 left-0 w-full bg-neue-canvas-bg rounded-lg transition flex justify-center items-center gap-3',
              {
                'opacity-0': draggingOver,
              }
            )}
          >
            <div
              className='h-10 px-3 py-[3px] text-neue-canvas-desktop-body text-[24px] leading-[1.4] transition bg-neue-canvas-fg-10 text-neue-canvas-fg-50 hover:bg-neue-canvas-fg-20 rounded-lg cursor-pointer'
              onClick={() => onAddText(section.id)}
            >
              Add text
            </div>
            <div
              className='h-10 px-3 py-[3px] text-neue-canvas-desktop-body text-[24px] leading-[1.4] transition bg-neue-canvas-fg-10 text-neue-canvas-fg-50 hover:bg-neue-canvas-fg-20 rounded-lg cursor-pointer'
              onClick={() => onAddImage(section.id)}
            >
              add image
            </div>
            <div className='text-neue-canvas-desktop-body text-[24px] leading-[1.4] text-neue-canvas-fg-50'>or</div>
            <div className='h-10 px-3 py-[3px] text-neue-canvas-desktop-body text-[24px] leading-[1.4] text-neue-canvas-fg-50 relative cursor-grabbing select-none'>
              drag a block
              <svg
                className='absolute top-0 left-0 w-full h-full pointer-events-none text-neue-canvas-fg-10'
                width='100%'
                height='100%'
                preserveAspectRatio='none'
              >
                <rect
                  x={0}
                  y={0}
                  width='100%'
                  height='100%'
                  rx={8}
                  ry={8}
                  fill='transparent'
                  vectorEffect='non-scaling-stroke'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeDasharray='8 6'
                  strokeLinecap='round'
                  // strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
        )}
      </animated.div>
    </div>
  );
};

export const MemoizedEmptySectionView = React.memo(
  EmptySectionView,
  createPropsEqualsChecker(['style'], 'EmptySectionView')
);
